import React from 'react';
import { Box, Card, CardContent, LinearProgress } from '@mui/material';
import { Typography } from '@mui/joy';
import ErrorRounded from '@mui-symbols-material/w200/ErrorRounded';
import SensorsRounded from '@mui-symbols-material/w300/SensorsRounded';
import { useTranslation } from 'react-i18next';
import { getIotStatusChipColors } from '../../config/iotConnectionStatusConfig';
import { connectionStatusIconFunc, connectionStatusTextFunc } from '../IoTConnectionWidget/connectionStatus';

interface Props {
    connectionStatus?: string;
    healthIndex?: number;
    remainingLifetime?: number;
}

const AssetIoTStatus = ({ connectionStatus, healthIndex, remainingLifetime }: Props) => {
    const { t } = useTranslation();

    const statusColors = getIotStatusChipColors(connectionStatus);
    const failureRisk = healthIndex ? 100 - healthIndex : 0;

    return (
        <Card sx={{
            border: "1px solid #cccccc",
            borderRadius: "4px",
            height: "100%",
            boxShadow: "none"
        }}>
            <CardContent sx={{ padding: "20px", height: "100%", display: "flex", flexDirection: "column" }}>
                {/* Header */}
                <Box sx={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                    <SensorsRounded sx={{ fontSize: 28, color: "#333" }} />
                    <Typography level="titleMedium" sx={{ fontSize: "18px", lineHeight: "25px", color: "#333" }}>
                        Status
                    </Typography>
                </Box>

                {/* Status Section */}
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 16px",
                    borderRadius: "3px",
                    backgroundColor: statusColors.bg,
                    marginBottom: "8px"
                }}>
                    <Box>
                        <Typography level="titleSmall" sx={{ fontSize: "16px", lineHeight: "22px", color: statusColors.text }}>
                            Status
                        </Typography>
                        <Box sx={{ paddingLeft: "8px" }}>
                            <Typography sx={{ fontSize: "24px", lineHeight: "32px", fontWeight: 400, color: statusColors.text }}>
                                {connectionStatusTextFunc({ connectionStatus: connectionStatus ?? '', t }) || t('Unknown')}
                            </Typography>
                        </Box>
                    </Box>
                    {connectionStatusIconFunc({ connectionStatus: connectionStatus ?? '', size: 80 })}
                </Box>

                {/* Remaining Useful Lifetime */}
                <Box sx={{
                    flex: 1,
                    borderTop: "1px solid #cccccc",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between"
                }}>
                    <Typography level="titleSmall" sx={{ fontSize: "16px", lineHeight: "22px", color: "#333" }}>
                        Remaining useful lifetime
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
                        <Box sx={{ flex: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={remainingLifetime ? (remainingLifetime / 365) * 100 : 0}
                                sx={{
                                    height: 8,
                                    borderRadius: "4px",
                                    backgroundColor: "#d9d9d9",
                                    '& .MuiLinearProgress-bar': {
                                        background: 'linear-gradient(90deg, #b74600 0%, #ff9800 50%, #82c18b 100%)',
                                        borderRadius: "4px"
                                    }
                                }}
                            />
                        </Box>
                        <Typography sx={{ fontSize: "42px", lineHeight: "48px", fontWeight: 400, color: "#333" }}>
                            {remainingLifetime ? `${remainingLifetime} days` : '—'}
                        </Typography>
                    </Box>
                </Box>

                {/* Health Index */}
                <Box sx={{
                    borderTop: "1px solid #cccccc",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between"
                }}>
                    <Typography level="titleSmall" sx={{ fontSize: "16px", lineHeight: "22px", color: "#333", marginBottom: "20px" }}>
                        Health index
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
                        <Box sx={{ flex: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={healthIndex || 0}
                                sx={{
                                    height: 8,
                                    borderRadius: "4px",
                                    backgroundColor: "#d9d9d9",
                                    '& .MuiLinearProgress-bar': {
                                        background: 'linear-gradient(90deg, #82c18b 0%, #ffd54f 50%, #ff6b6b 100%)',
                                        borderRadius: "4px"
                                    }
                                }}
                            />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <Box sx={{ textAlign: "center", paddingLeft: "8px", paddingBottom: "3px" }}>
                                <Typography sx={{ fontSize: "42px", lineHeight: "48px", fontWeight: 400, color: "#333" }}>
                                    {healthIndex ? `${healthIndex}%` : '—'}
                                </Typography>
                                <Typography sx={{ fontSize: "9px", lineHeight: "12px", color: "#333" }}>
                                    failure risk
                                </Typography>
                            </Box>
                            {failureRisk > 70 && (
                                <ErrorRounded sx={{ fontSize: 24, color: "#cc0000" }} />
                            )}
                        </Box>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default AssetIoTStatus;
